import { useState, useCallback } from "react";
import { UsersService } from "JS/Services/User";
import { ILogin, User } from "JS/Models";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { setLoggedInUser } from "JS/Redux/User";
import { appConstants } from "JS/Helpers/Constants";
import { GiftService } from "JS/Services/Gift";
import moment from "moment";
import { EventsHelper } from "JS/Services/Events/EventsHelper";
import {
    getUserFirstName,
    getUserLastName,
    setLocalStorageUserName,
} from "JS/Helpers/LocalStorageHelpers";
import { loggingFirebaseService } from "JS/Services/Firebase/LoggingService";
import { captureSentryError } from "JS/Helpers/SentryHelper";
import { useSnackbar } from "JS/Helpers/Snackbar";

const service = new UsersService();

export default service;
export const useLogin = () => {
    const [loading, setLoading] = useState(false);
    const [loginResponse, setLoginResponse] = useState<User>(null);
    const { enqueueSnackbar } = useSnackbar();

    const login = useCallback((loginData: ILogin) => {
        setLoading(true);
        service
            .login(loginData)
            .then((val) => setLoginResponse(val))
            .catch((err) =>
                enqueueSnackbar(err.message, {
                    variant: "error",
                }),
            )
            .finally(() => setLoading(false));
    }, []);

    return {
        login,
        loginResponse,
        loading,
    };
};

export const useJwtToken = () => {
    const disableJwtToken = async () => service.disableJwtToken();

    return {
        disableJwtToken,
    };
};

export const useGlobalLoggedInUser = () => {
    const dispatch = useAppDispatch();

    return {
        loggedInUser: useAppSelector((state) => state?.user)?.loggedInUser,
        setLoggedInUserData: (user: User) => {
            dispatch(setLoggedInUser(user));
        },
    };
};

export const useGetLatestUsername = () => {
    const giftService = new GiftService();

    const getLatestUsername = () => {
        giftService
            .getUserName()
            .then((response) => {
                loggingFirebaseService.logRealtimeAnalysis(
                    "UsernameResponse",
                    {
                        response: JSON.stringify(response.data),
                    },
                    "UsernameFetch",
                );
                const firstName = response.data.first_name;
                const lastName = response.data.last_name;
                if (
                    firstName &&
                    lastName &&
                    firstName !== "undefined" &&
                    lastName !== "undefined"
                ) {
                    setLocalStorageUserName(firstName, lastName);
                } else {
                    const err = new Error(
                        "Received undefined username in response of API call",
                    );
                    loggingFirebaseService.logRealtimeAnalysis(
                        "ReceivedUndefinedUsernameFromApiResponse",
                        null,
                        "UsernameFetch",
                    );
                    captureSentryError(err, {
                        location: "useGetLatestUsername",
                    });
                }
            })
            .catch((err) => {
                loggingFirebaseService.logRealtimeAnalysis(
                    "ApiError",
                    null,
                    "UsernameFetch",
                );
                captureSentryError(err, {
                    location: "useGetLatestUsername",
                });
            });
    };

    const syncUsername = () => {
        const syncTime = +localStorage.getItem(
            appConstants.localStorage.usernameSyncTime,
        );
        const have24HoursPassed =
            moment.now() - syncTime >= EventsHelper.ONE_DAY;
        const userFirstName = getUserFirstName();
        const userLastName = getUserLastName();

        if (
            !userFirstName ||
            !userLastName ||
            userFirstName === "undefined" ||
            userLastName === "undefined" ||
            have24HoursPassed
        ) {
            loggingFirebaseService.logRealtimeAnalysis(
                "Username24Hrs",
                null,
                "UsernameFetch",
            );
            getLatestUsername();
        }
    };
    return {
        getLatestUsername,
        syncUsername,
    };
};
